/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import { useState, useMemo } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Token, TokenDefinition } from '@/components/ui/TokenizedInput/types';
import { TokenEditForm } from './TokenEditForm';
import { Calendar, Type, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import * as React from 'react';
import {
  operationDefinitions,
  getDefaultOperationsForType,
} from './operations';
import usePromise from '@/hooks/usePromise';
import { Skeleton } from '@/components/ui/skeleton';
import { DateDisplay } from '@/components/ui/DateDisplay';

// TODO: render title (mousover) description of operation

const getFieldTypeIcon = (type: string) => {
  switch (type) {
    case 'date':
      return Calendar;
    case 'text':
      return Type;
    case 'select':
      return ChevronDown;
    default:
      return Type;
  }
};

const getTokenIcon = (
  _token: Token,
  tokenDefinition: TokenDefinition,
  selectedOption?: any,
) => {
  if (selectedOption && tokenDefinition.getOptionIcon) {
    const OptionIcon = tokenDefinition.getOptionIcon(selectedOption);
    if (OptionIcon) return OptionIcon;
  }

  if (tokenDefinition.icon) {
    return tokenDefinition.icon;
  }

  return getFieldTypeIcon(tokenDefinition.type);
};

interface TokenElementProps<TData = any> {
  token: Token;
  tokenDefinition: TokenDefinition<TData>;
  index: number;
  focused: boolean;
  onKeyDown: (e: React.KeyboardEvent, tokenIndex: number) => void;
  onClick: (tokenIndex: number) => void;
  onRemove: (tokenIndex: number) => void;
  onTokenSave: (tokenIndex: number, updatedToken: Token) => void;
}

export function TokenElement({
  token,
  tokenDefinition,
  index,
  focused,
  onKeyDown,
  onClick,
  onRemove,
  onTokenSave,
}: TokenElementProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = () => {
    onClick(index);
    setIsOpen(true);
  };

  const handleRemove = () => {
    onRemove(index);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(true);
    } else {
      onKeyDown(e, index);
    }
  };

  const handleSave = (updatedToken: Token) => {
    onTokenSave(index, updatedToken);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  // ???: why empty
  const handleCloseAutoFocus = () => {
    // Let the focus return to the token naturally
    // This is the key to fixing the focus restoration issue
  };

  const data = usePromise(
    useMemo(
      () =>
        tokenDefinition.fetchOption?.(token.value) ||
        Promise.resolve(
          tokenDefinition.type === 'select' && tokenDefinition.options
            ? tokenDefinition.options.find((option) => {
                const optionValue =
                  tokenDefinition.getOptionValue?.(option) ||
                  option.value ||
                  option;
                return optionValue === token.value;
              })
            : token.value,
        ),
      [tokenDefinition, token],
    ),
  );

  const selectedOption =
    tokenDefinition.type === 'select' && tokenDefinition.options
      ? tokenDefinition.options.find((option) => {
          const optionValue =
            tokenDefinition.getOptionValue?.(option) || option.value || option;
          return optionValue === token.value;
        })
      : undefined;

  const TokenIcon = getTokenIcon(token, tokenDefinition, selectedOption);

  const defaultOperations =
    tokenDefinition.operations ||
    getDefaultOperationsForType(tokenDefinition.type);
  const defaultOperation = defaultOperations[0];
  const shouldShowOperation = token.operation !== defaultOperation;

  const operationDef = operationDefinitions.find(
    (op) => op.key === token.operation,
  );
  const operationText =
    operationDef?.shortLabel || operationDef?.label || token.operation;

  const tokenElement = tokenDefinition.renderToken ? (
    tokenDefinition.renderToken(
      token,
      tokenDefinition,
      selectedOption,
      handleRemove,
    )
  ) : (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium',
        'border-border text-foreground border bg-transparent',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:ring-ring/20 focus:ring-2 focus:ring-offset-0 focus:outline-none',
        'cursor-pointer transition-colors',
      )}
    >
      <TokenIcon className="h-3 w-3" />
      <span className="font-medium">
        {tokenDefinition.label || token.field}
      </span>
      {shouldShowOperation && (
        <span className="text-muted-foreground">{operationText}</span>
      )}
      {data ? (
        tokenDefinition.type === 'date' ? (
          <DateDisplay value={data} dateFormat="yyyy-MM-dd" />
        ) : (
          <span className="">
            {tokenDefinition.getOptionLabel?.(data) ||
              String(data?.label ?? data)}
          </span>
        )
      ) : (
        <Skeleton />
      )}
    </div>
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          tabIndex={focused ? 0 : -1}
          data-token-index={index}
          data-roving-index={index}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
        >
          {tokenElement}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80" onCloseAutoFocus={handleCloseAutoFocus}>
        <TokenEditForm
          token={token}
          tokenDefinition={tokenDefinition}
          operationDefinitions={operationDefinitions}
          onSave={handleSave}
          onCancel={handleCancel}
          onDelete={handleRemove}
        />
      </PopoverContent>
    </Popover>
  );
}
